// "Manual Loan Exclusions" rule builder (see manual_loan_exclusions.py /
// excludable_files.py / exclusion_rules.py). A rule says "drop rows from
// file X (optional: specific sheet(s)) where column Y == value Z", optionally
// also adding column B of the matched rows to that platform's Outstanding
// Bids. Cascading dropdowns: file -> sheet(s)/identifier column (from
// GET files/:fileKey/columns) -> optional bids column (numeric columns of
// the same sheet(s)). "Add" always previews first (`exclusions_preview`
// job, dry-run) before the rule lands in the local draft table - the actual
// save (`exclusions_save` job) only happens on "Save Selection", and only
// sends the rule's core fields
// (file_key/sheet_keys/identifier_column/identifier_value/add_to_bids/
// bids_column/note) - id/created_at are server-assigned defaults
// (exclusion_rules.ManualExclusionRule), no need to round-trip them.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import JobProgress from '../JobProgress';
import LoadingState from '../../common/LoadingState';
import EmptyState from '../../common/EmptyState';
import { apiErrorDetail, readErrorDetail, useWizardJob } from '../jobs';
import errorMessage from '../../../helpers/errorMessage';

const CORE_FIELDS = ['file_key', 'sheet_keys', 'identifier_column', 'identifier_value', 'add_to_bids', 'bids_column', 'note'];

const canonicalize = (rule) => {
  const out = {};
  CORE_FIELDS.forEach((f) => {
    let v = rule[f];
    if (f === 'sheet_keys' && Array.isArray(v)) v = [...v].sort();
    if (v === undefined) v = null;
    out[f] = v;
  });
  return out;
};

const rulesEqual = (a, b) => {
  if (a.length !== b.length) return false;
  const norm = (list) => list.map((r) => JSON.stringify(canonicalize(r))).sort();
  const na = norm(a);
  const nb = norm(b);
  return na.every((v, i) => v === nb[i]);
};

let localIdSeq = 0;
const nextLocalId = () => `draft-${Date.now()}-${localIdSeq++}`;

const ManualExclusionsPanel = ({ reportingId, setLoading, onChange }) => {
  const [report, setReport] = useState(null);
  const [files, setFiles] = useState([]);
  const [savedRules, setSavedRules] = useState([]);
  const [draftRules, setDraftRules] = useState([]);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // --- add-rule form state ---
  const [fileKey, setFileKey] = useState(null);
  const [columnsByFile, setColumnsByFile] = useState(null); // {sheets: {sheet_key: {label, columns, numeric_columns, error?}}}
  const [sheetKeys, setSheetKeys] = useState(null); // null = all sheets of this file
  const [identifierColumn, setIdentifierColumn] = useState(null);
  const [identifierValue, setIdentifierValue] = useState('');
  const [addToBids, setAddToBids] = useState(false);
  const [bidsColumn, setBidsColumn] = useState(null);
  const [note, setNote] = useState('');
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const { runJob, activeJob, busy } = useWizardJob();

  const fetchAll = useCallback(async () => {
    if (!reportingId) return;
    setLoading(true);
    try {
      const [reportRes, filesRes] = await Promise.all([
        axiosWithAuth.get(`/compartment-report/reportings/${reportingId}/manual-exclusions`),
        axiosWithAuth.get(`/compartment-report/reportings/${reportingId}/manual-exclusions/files`),
      ]);
      setReport(reportRes.data);
      setSavedRules(reportRes.data.rules || []);
      setDraftRules((reportRes.data.rules || []).map((r) => ({ ...r, _localId: r.id })));
      setFiles(filesRes.data.files || []);
      setError(null);
    } catch (err) {
      console.error('[ManualExclusionsPanel] fetch error:', errorMessage(err));
      setError(readErrorDetail(err, 'Failed to load manual exclusions.'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportingId]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const resetForm = () => {
    setFileKey(null);
    setColumnsByFile(null);
    setSheetKeys(null);
    setIdentifierColumn(null);
    setIdentifierValue('');
    setAddToBids(false);
    setBidsColumn(null);
    setNote('');
    setPreviewResult(null);
    setPreviewError(null);
  };

  const handleFileChange = async (key) => {
    setFileKey(key);
    setSheetKeys(null);
    setIdentifierColumn(null);
    setBidsColumn(null);
    setColumnsByFile(null);
    setPreviewResult(null);
    setPreviewError(null);
    if (!key || !reportingId) return;
    setLoading(true);
    try {
      const res = await axiosWithAuth.get(
        `/compartment-report/reportings/${reportingId}/manual-exclusions/files/${key}/columns`
      );
      setColumnsByFile(res.data.sheets || {});
    } catch (err) {
      console.error('[ManualExclusionsPanel] columns fetch error:', errorMessage(err));
      setError(apiErrorDetail(err, 'Failed to load columns for this file.'));
    } finally {
      setLoading(false);
    }
  };

  const fileSpec = files.find((f) => f.file_key === fileKey);
  const isMultiSheet = fileSpec && Array.isArray(fileSpec.sheets) && fileSpec.sheets.length > 1;

  // Columns available for identifier/bids dropdowns: union across the
  // currently-targeted sheet(s) (all sheets if none explicitly picked).
  const { identifierOptions, numericOptions } = useMemo(() => {
    if (!columnsByFile) return { identifierOptions: [], numericOptions: [] };
    const keys = isMultiSheet && sheetKeys && sheetKeys.length > 0 ? sheetKeys : Object.keys(columnsByFile);
    const cols = new Set();
    const numeric = new Set();
    keys.forEach((k) => {
      const sheet = columnsByFile[k];
      if (!sheet) return;
      (sheet.columns || []).forEach((c) => cols.add(c));
      (sheet.numeric_columns || []).forEach((c) => numeric.add(c));
    });
    return {
      identifierOptions: Array.from(cols).map((c) => ({ label: c, value: c })),
      numericOptions: Array.from(numeric).map((c) => ({ label: c, value: c })),
    };
  }, [columnsByFile, sheetKeys, isMultiSheet]);

  const sheetOptions = fileSpec && Array.isArray(fileSpec.sheets) ? fileSpec.sheets.map((s) => ({ label: s.label, value: s.sheet_key })) : [];

  const canPreview = fileKey && identifierColumn && identifierValue.trim() && (!addToBids || bidsColumn);

  const handlePreview = async () => {
    if (!reportingId || !canPreview) return;
    setPreviewing(true);
    setPreviewResult(null);
    setPreviewError(null);
    try {
      // No-match / unknown-column fail the job with error_status 404 and the
      // old endpoint's detail text as `error`.
      const job = await runJob(reportingId, 'exclusions_preview', {
        file_key: fileKey,
        sheet_keys: isMultiSheet ? sheetKeys : null,
        identifier_column: identifierColumn,
        identifier_value: identifierValue,
        add_to_bids: addToBids,
        bids_column: addToBids ? bidsColumn : null,
      });
      setPreviewResult(job.result);
    } catch (err) {
      if (err.cancelled) return;
      console.error('[ManualExclusionsPanel] preview error:', err.detail || errorMessage(err));
      setPreviewError(apiErrorDetail(err, 'Preview failed.'));
    }
    setPreviewing(false);
  };

  const handleAddToDraft = () => {
    if (!previewResult) return;
    const rule = {
      _localId: nextLocalId(),
      file_key: fileKey,
      file_label: fileSpec ? fileSpec.label : fileKey,
      sheet_keys: isMultiSheet ? sheetKeys : null,
      identifier_column: identifierColumn,
      identifier_value: identifierValue,
      add_to_bids: addToBids,
      bids_column: addToBids ? bidsColumn : null,
      note: note || null,
      matched_count: previewResult.matched_count,
      platform_counts: previewResult.platform_counts,
      bids_by_platform: previewResult.bids_by_platform,
    };
    setDraftRules((prev) => [...prev, rule]);
    resetForm();
  };

  const removeDraftRule = (localId) => {
    setDraftRules((prev) => prev.filter((r) => r._localId !== localId));
  };

  const dirty = !rulesEqual(draftRules, savedRules);

  const handleSave = async () => {
    if (!reportingId || !dirty) return;
    setSaving(true);
    try {
      const payload = {
        rules: draftRules.map((r) => ({
          file_key: r.file_key,
          sheet_keys: r.sheet_keys || null,
          identifier_column: r.identifier_column,
          identifier_value: r.identifier_value,
          add_to_bids: r.add_to_bids,
          bids_column: r.bids_column || null,
          note: r.note || null,
        })),
      };
      const job = await runJob(reportingId, 'exclusions_save', payload);
      const saved = job.result;
      setReport(saved);
      setSavedRules(saved.rules || []);
      setDraftRules((saved.rules || []).map((r) => ({ ...r, _localId: r.id })));
      setError(null);
      if (onChange) onChange(saved);
    } catch (err) {
      if (err.cancelled) return;
      console.error('[ManualExclusionsPanel] save error:', err.detail || errorMessage(err));
      setError(apiErrorDetail(err, 'Failed to save manual exclusions.'));
    }
    setSaving(false);
  };

  const panelJob = activeJob && (activeJob.action === 'exclusions_preview' || activeJob.action === 'exclusions_save') ? activeJob : null;

  if (!report) return error ? <div className="ex-inline-error">{error}</div> : <LoadingState label="Loading manual exclusions" />;

  return (
    <div>
      <div className="cr-summary-line">
        <span>
          Currently saved: {report.total_count} matched row(s) - {(report.total_bids_amount || 0).toLocaleString('en-IE', { minimumFractionDigits: 2 })}{' '}
          in Outstanding Bids
        </span>
      </div>

      {error && <div className="ex-inline-error mb-3">{error}</div>}

      <div className="cr-card mb-3">
        <h4 className="cr-card-title cr-card-title-block">Add Rule</h4>
        <div className="cr-form-row align-items-start">
          <div>
            <div className="cr-field-label">File</div>
            <Dropdown
              value={fileKey}
              options={files.map((f) => ({ label: f.label + (f.has_upload === false ? ' (not uploaded)' : ''), value: f.file_key }))}
              onChange={(e) => handleFileChange(e.value)}
              placeholder="Select file"
              style={{ minWidth: '16rem' }}
            />
          </div>

          {isMultiSheet && (
            <div>
              <div className="cr-field-label">Sheet(s) (empty = all)</div>
              <MultiSelect
                value={sheetKeys || []}
                options={sheetOptions}
                onChange={(e) => setSheetKeys(e.value.length ? e.value : null)}
                placeholder="All sheets"
                style={{ minWidth: '14rem' }}
              />
            </div>
          )}

          <div>
            <div className="cr-field-label">Identifier Column</div>
            <Dropdown
              value={identifierColumn}
              options={identifierOptions}
              onChange={(e) => setIdentifierColumn(e.value)}
              placeholder="Select column"
              filter
              disabled={!columnsByFile}
              style={{ minWidth: '14rem' }}
            />
          </div>

          <div>
            <div className="cr-field-label">Identifier Value</div>
            <InputText value={identifierValue} onChange={(e) => setIdentifierValue(e.target.value)} placeholder="Value to match" />
          </div>

          <div className="flex align-items-center gap-2 cr-checkbox-field">
            <Checkbox checked={addToBids} onChange={(e) => setAddToBids(e.checked)} inputId="addToBids" />
            <label htmlFor="addToBids">Add to Outstanding Bids</label>
          </div>

          {addToBids && (
            <div>
              <div className="cr-field-label">Bids Column (numeric)</div>
              <Dropdown
                value={bidsColumn}
                options={numericOptions}
                onChange={(e) => setBidsColumn(e.value)}
                placeholder="Select column"
                filter
                disabled={!columnsByFile}
                style={{ minWidth: '14rem' }}
              />
            </div>
          )}

          <div style={{ flexBasis: '100%' }}>
            <div className="cr-field-label">Note (optional)</div>
            <InputText value={note} onChange={(e) => setNote(e.target.value)} style={{ width: '100%' }} />
          </div>
        </div>

        <div className="flex flex-wrap align-items-center gap-3 mt-3">
          <Button label="Preview" className="p-button-outlined" onClick={handlePreview} disabled={!canPreview || previewing || busy} loading={previewing} />
          <Button label="Add to Draft" onClick={handleAddToDraft} disabled={!previewResult || previewing} />
        </div>
        {previewing && panelJob && <JobProgress job={panelJob} style={{ marginTop: '0.75rem' }} />}

        {previewError && <div className="ex-inline-error mt-2">{previewError}</div>}
        {previewResult && (
          <div className="cr-tag-row mt-2">
            <Tag severity="info" value={`${previewResult.matched_count} matched`} />{' '}
            {previewResult.column_missing && <Tag severity="warning" value="Column missing in some sheet(s)" />}{' '}
            {Object.entries(previewResult.platform_counts || {}).map(([p, c]) => (
              <Tag key={p} severity="info" value={`${p}: ${c}`} style={{ marginLeft: '0.4rem' }} />
            ))}
            {addToBids &&
              Object.entries(previewResult.bids_by_platform || {}).map(([p, amt]) => (
                <Tag key={`bids-${p}`} severity="success" value={`${p} bids: ${amt.toLocaleString('en-IE', { minimumFractionDigits: 2 })}`} style={{ marginLeft: '0.4rem' }} />
              ))}
          </div>
        )}
      </div>

      <h4 className="cr-subsection-title">Draft Selection ({draftRules.length} rule(s))</h4>
      {draftRules.length === 0 ? (
        <EmptyState icon="pi-filter-slash" title="No rules yet." compact />
      ) : (
        <DataTable value={draftRules} dataKey="_localId" size="small" showGridlines stripedRows>
          <Column field="file_label" header="File" body={(r) => r.file_label || r.file_key} />
          <Column header="Sheet(s)" body={(r) => (r.sheet_keys && r.sheet_keys.length ? r.sheet_keys.join(', ') : 'All')} />
          <Column field="identifier_column" header="Column" />
          <Column field="identifier_value" header="Value" />
          <Column header="Bids" body={(r) => (r.add_to_bids ? r.bids_column : '-')} />
          <Column header="Matched" headerClassName="cr-num" bodyClassName="cr-num" body={(r) => (r.matched_count ?? '-')} />
          <Column field="note" header="Note" />
          <Column header="" body={(r) => <Button icon="pi pi-trash" className="p-button-danger p-button-text" onClick={() => removeDraftRule(r._localId)} />} />
        </DataTable>
      )}

      <div className="flex flex-wrap align-items-center gap-3 mt-3">
        <Button label="Save Selection" onClick={handleSave} disabled={!dirty || saving || busy} loading={saving} />
        {dirty ? <Tag severity="warning" value="Unsaved changes" /> : <Tag severity="success" value="Saved" />}
      </div>
      {saving && panelJob && <JobProgress job={panelJob} style={{ marginTop: '0.75rem' }} />}
    </div>
  );
};

export default ManualExclusionsPanel;
